import React from 'react';
import parse from 'html-react-parser';
import { Box, Button, Stack, Typography } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { ManualMetricItem } from '../../../_types/policy';
import { CheckCircleOutlineOutlined, MenuBookOutlined, WarningAmberOutlined } from '@mui/icons-material';
import Carousel from 'react-material-ui-carousel';
import { error, neutral, primary, success, warning } from '_lex/_foundations/phxColors';
import datetimeHelper from 'helpers/datetimeHelper';
import { useLocation, useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';

type ManualItemProps = {
  metric: ManualMetricItem;
};

export default function ManualItem({ metric }: ManualItemProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const metricCounts = {
    pending: metric.pendingCount,
    draft: metric.draftCount,
    approved: metric.approvedCount,
  };

  function parseContextUri(contextUri: string) {
    const parts = contextUri.split('/');

    if (parts.length >= 5 && parts[2] === 'manuals' && parts[4] === 'content') {
      return {
        manual: parts[3],
        policyId: parts[5],
      };
    }

    return null;
  }

  const handleProcessUpdate = (contextUri: string) => {
    const result = parseContextUri(contextUri);
    if (result) {
      navigate(`/admin/policy/draft-manuals/${result.manual}/policy:${result.policyId}/review-updates`, { state: { from: location.pathname, policyId: result.policyId } });
    } else {
      enqueueSnackbar(t('dashboard.policy.manualMetrics.manualMetricItem.invalidPolicyLink'), { variant: 'error' });
    }
  };
  return (
    <Box
      className='manual-metrics-item'
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 2px 460px',
        gap: 4,
        alignItems: 'start',
      }}
    >
      <Stack spacing={3}>
        <Stack>
          <Stack
            direction='row'
            spacing={2}
          >
            <MenuBookOutlined sx={{ color: neutral[400] }} />
            <Typography
              variant='subtitle1'
              sx={{ lineHeight: '24px' }}
            >
              {metric.name}
            </Typography>
          </Stack>
          {metric.lastPublishedAt && (
            <Stack
              direction='row'
              spacing={2}
            >
              <Typography sx={{ color: neutral[500] }}>
                {t('dashboard.policy.manualMetrics.manualMetricItem.lastPublishedAt')}:{' '}
                {datetimeHelper.formatDate(metric.lastPublishedAt)}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 2,
          }}
        >
          {Object.entries(metricCounts).map(([key, value]) => (
            <Box
              key={`metric_${key}`}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
                px: 3,
                py: 2,
                border: `solid 1px ${neutral[200]}`,
                borderRadius: 1,
                backgroundColor: neutral[50],
              }}
            >
              <Typography
                sx={{
                  fontSize: 32,
                  fontWeight: 500,
                  color: primary[900],
                }}
              >
                {value}
              </Typography>
              <Typography variant='caption'>
                {parse(t(`dashboard.policy.manualMetrics.manualMetricItem.${key}`))}
              </Typography>
            </Box>
          ))}
        </Box>
      </Stack>
      <Box sx={{ color: neutral[200] }}></Box>
      <Box sx={{ minHeight: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1 }}>
        {metric.policyUpdates.length === 0 ? (
          <Box
            sx={{
              height: 64,
              width: 320,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 3,
              px: 3,
              py: 2,
              border: `solid 1px ${neutral[300]}`,
              borderRadius: 2,
              backgroundColor: success[25],
            }}
          >
            <Stack
              direction='row'
              spacing={2}
            >
              <CheckCircleOutlineOutlined />
              <Typography>{parse(t(`dashboard.policy.manualMetrics.manualMetricItem.noUpdates`))}</Typography>
            </Stack>
          </Box>
        ) : (
          <Box sx={{ flex: 1 }}>
            <Carousel
              className='policy-updates'
              autoPlay={false}
              animation='slide'
              navButtonsAlwaysVisible
              navButtonsProps={{
                style: {
                  backgroundColor: 'transparent',
                  color: neutral[400],
                },
              }}
              sx={{
                '&>div:first-of-type>div>div>div>div': {
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                },
              }}
            >
              {metric.policyUpdates.slice(0, 5).map((policyUpdate, idx) => (
                <Box
                  className='policy-update-item'
                  key={policyUpdate.id || idx}
                  sx={{
                    height: 110,
                    width: 320,
                    px: 3,
                    py: 2,
                    border: `solid 1px ${warning[500]}`,
                    borderRadius: 2,
                    backgroundColor: neutral[25],
                  }}
                >
                  <Stack spacing={2}>
                    <Stack>
                      <Stack
                        direction='row'
                        spacing={2}
                      >
                        <WarningAmberOutlined sx={{ color: error[700] }} />
                        <Typography
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {parse(policyUpdate?.title || 'Untitled update')}
                        </Typography>
                      </Stack>
                      {policyUpdate.updatedAt && (
                        <Stack
                          direction='row'
                          spacing={2}
                        >
                          <Typography
                            variant='caption'
                            sx={{ color: neutral[500] }}
                          >
                            {t('dashboard.policy.manualMetrics.manualMetricItem.updatedAt')}:{' '}
                            {datetimeHelper.formatDate(policyUpdate.updatedAt)}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                    <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                      <Button
                        color='secondary'
                        size='small'
                        onClick={() => handleProcessUpdate(policyUpdate.contentUri)}
                      >
                        {t('dashboard.policy.manualMetrics.manualMetricItem.processUpdate')}
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Carousel>
          </Box>
        )}
      </Box>
    </Box>
  );
}
